import { queryKeys } from "@healthos/client-data";
import { FLAGS } from "@healthos/feature-flags";
import type { api } from "@healthos/schemas";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { client, jsonOrThrow } from "../api/healthos";
import { reportDatadogFeatureFlag } from "./datadog";
import { setDarkModeAllowed } from "./theme";

/**
 * Whether this member may use the dark theme, from the member-scoped
 * `enableDarkMode` flag. `undefined` while the answer is still loading, so a
 * caller can tell "not yet known" from "no".
 */
export function useDarkModeAccess(): boolean | undefined {
  const { data } = useQuery({
    queryKey: queryKeys.darkMode,
    queryFn: async () =>
      jsonOrThrow<api.DarkModeAccessResponse>(
        await client().api.me["dark-mode"].$get(),
        "GET /api/me/dark-mode",
      ),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data?.enabled;
}

/**
 * Mount once in the signed-in shell: hands each answer to the theme store,
 * which forces light while the flag is off. A failed fetch leaves the cached
 * answer in place, so a network blip never flips a member's theme.
 */
export function useDarkModeGate(): void {
  const allowed = useDarkModeAccess();
  useEffect(() => {
    if (allowed === undefined) return;
    setDarkModeAllowed(allowed);
    reportDatadogFeatureFlag(FLAGS.enableDarkMode.key, allowed);
  }, [allowed]);
}
